/**
 * MapLegend Component
 * Floating color legend for choropleth map with gradient + value ranges
 */

import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { formatNumber, getIndicatorLabel, IndicatorType } from './mapUtils';

interface MapLegendProps {
  breaks: number[];
  colors: string[];
  indicator: IndicatorType;
}

const MapLegend: React.FC<MapLegendProps> = ({ breaks, colors, indicator }) => {
  if (breaks.length === 0) return null;

  // Build legend items with ranges
  const items: { color: string; label: string }[] = [];

  for (let i = 0; i < breaks.length; i++) {
    const from = i === 0 ? 0 : breaks[i - 1] + 1;
    const to = breaks[i];
    const color = colors[i] || colors[colors.length - 1];

    if (indicator === 'gender_parity') {
      items.push({
        color,
        label: `${(from / 100).toFixed(2)} – ${(to / 100).toFixed(2)}`,
      });
    } else {
      items.push({
        color,
        label: `${formatNumber(from)} – ${formatNumber(to)}`,
      });
    }
  }

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        bottom: 30,
        right: 10,
        zIndex: 1000,
        p: 1.5,
        borderRadius: 2,
        minWidth: 160,
        maxWidth: 220,
        backgroundColor: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, mb: 1, fontSize: '0.75rem', color: '#333' }}
      >
        {getIndicatorLabel(indicator)}
      </Typography>

      {items.map((item, idx) => (
        <Box
          key={idx}
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 0.3,
          }}
        >
          <Box
            sx={{
              width: 18,
              height: 14,
              backgroundColor: item.color,
              border: '1px solid rgba(0,0,0,0.2)',
              borderRadius: 0.5,
              mr: 1,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="caption"
            sx={{ fontSize: '0.68rem', color: '#555', lineHeight: 1.3 }}
          >
            {item.label}
          </Typography>
        </Box>
      ))}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: 0.5,
          pt: 0.5,
          borderTop: '1px solid #eee',
        }}
      >
        <Box
          sx={{
            width: 18,
            height: 14,
            backgroundColor: '#CCCCCC',
            border: '1px solid rgba(0,0,0,0.2)',
            borderRadius: 0.5,
            mr: 1,
            flexShrink: 0,
          }}
        />
        <Typography
          variant="caption"
          sx={{ fontSize: '0.68rem', color: '#999', lineHeight: 1.3 }}
        >
          No data
        </Typography>
      </Box>
    </Paper>
  );
};

export default MapLegend;
